<template>
  <VContainer class="billing-page">
    <VRow v-if="isLoading">
      <div class="d-flex flex-column align-center justify-center py-4 w-100">
        <VProgressCircular indeterminate color="primary" />
      </div>
    </VRow>
    <VRow v-else>
      <VCol cols="12">
        <div class="billing-page__content">
          <div class="hr"></div>
          <div class="current-plan">
            <div class="current-plan__title">
              Current Plan:
              <span class="text-main-color">{{ application.curencyCode }}{{ application.amount }} / year</span>
            </div>
            <div class="current-plan__text">
              Renews automatically from (<span class="text-main-color">{{ application.renewsDate }}</span
              >)
            </div>
          </div>
          <div class="payment-method">
            <div class="payment-method__title">Payment Method</div>
            <div class="payment-method__text">
              {{ application.cardData.cardBrand }} ending in {{ application.cardData.last4Digits }} <br />Expires
              {{ application.cardData.expireMonth }}/{{ application.cardData.expireYear }}
            </div>
            <div>
              <button class="main-btn" @click="handleShowUpdatePaymentMethod()">Update your Payment Method</button>
            </div>
          </div>
          <div class="billing-history">
            <div class="billing-history__title">Billing History</div>
            <div class="billing-history__text">View and download your past receipts.</div>
            <table class="table">
              <tbody v-if="application.receiptData.length">
                <tr v-for="item in application.receiptData" :key="item.paymentDate">
                  <td>
                    <div>{{ item.paymentDate }}</div>
                  </td>
                  <td>
                    <div class="d-flex flex-column align-center justify-center">
                      {{ item.currencyCode }}{{ item.amount }}
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center justify-center gap-10">
                      <VBtn
                        icon
                        variant="text"
                        size="small"
                        color="primary"
                        @click="handleViewReceipt(item)"
                        :disabled="downloadingId === item.paymentId"
                        :title="'View Receipt'"
                        class="action-btn"
                      >
                        <VIcon>mdi-eye</VIcon>
                        <VTooltip activator="parent" location="top">View Receipt</VTooltip>
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="small"
                        :color="downloadingId === item.paymentId ? 'grey' : 'success'"
                        @click="downloadReceipt(item)"
                        :disabled="downloadingId === item.paymentId"
                        :loading="downloadingId === item.paymentId"
                        class="action-btn"
                      >
                        <VIcon>
                          {{ downloadingId === item.paymentId ? 'mdi-loading' : 'mdi-download' }}
                        </VIcon>
                        <VTooltip activator="parent" location="top">
                          {{ downloadingId === item.paymentId ? 'Downloading...' : 'Download Receipt' }}
                        </VTooltip>
                      </VBtn>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td>
                    <div class="d-flex gap-10 my-3">
                      <VIcon icon="mdi-information-slab-circle-outline" size="24" color="primary"></VIcon>
                      <span>Records not found!</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <UpdatePaymentMethod v-if="isShowUpdatePaymentMethod" :customerId="application.customerId" />
          <div class="auto-renewal">
            <div class="auto-renewal__title">Auto-Renewal</div>
            <div class="auto-renewal__text">
              Please <u @click="navigateTo('Contact Us')" class="cursor-pointer">contact us</u> if you wish to cancel
              this service
            </div>
            <div class="auto-renewal__note">
              You’ll keep access to all features until the end of your current billing period.
            </div>
          </div>
        </div>
      </VCol>
    </VRow>
  </VContainer>
  <ViewRecieptModal v-model="isShowViewReceiptModal" :receiptId="receiptId" />
</template>

<script>
import { mapActions } from 'vuex';
import html2pdf from 'html2pdf.js';
// Utils
import { formatBillingData } from '@/utils/formatData';
// Service
import applicationService from '@/services/application-service/application.service';
// Components
import ViewRecieptModal from '@/components/modals/ViewRecieptModal/ViewRecieptModal.vue';
import UpdatePaymentMethod from '@/components/common/UpdatePaymentMethod/UpdatePaymentMethod.vue';
export default {
  name: 'BillingPage',
  components: {
    ViewRecieptModal,
    UpdatePaymentMethod
  },
  data() {
    return {
      isLoading: true,
      isShowViewReceiptModal: false,
      isShowUpdatePaymentMethod: false,
      receiptId: '',
      downloadingId: null,
      downloadStatus: null,
      application: {
        amount: 0,
        renewsDate: '',
        curencyCode: '',
        customerId: '',
        cardData: {
          cardBrand: '',
          expireMonth: 0,
          expireYear: 0,
          last4Digits: ''
        },
        receiptData: []
      }
    };
  },
  mounted() {
    this.getBillingInfo();
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),

    async getBillingInfo() {
      this.isLoading = true;
      try {
        const { application } = await applicationService.getBillingInfo();
        // console.log(application);
        this.application = formatBillingData(application);
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoading = false;
      }
    },

    // View Receipt
    handleViewReceipt(item) {
      if (!item?.paymentId) {
        return;
      }
      this.isShowViewReceiptModal = true;
      this.receiptId = item.paymentId;
    },

    navigateTo(pageName) {
      if (!pageName) {
        return;
      }
      this.$router.push({ name: pageName });
    },

    async downloadReceipt(item) {
      if (!item?.paymentId) {
        this.showDownloadStatus('error', 'Invalid receipt ID');
        return;
      }

      this.downloadingId = item.paymentId;
      this.downloadStatus = null;

      try {
        // Fetch receipt data
        const { receipt } = await applicationService.getReceipt({
          id: item.paymentId
        });

        // Format date for filename
        const paymentDate = item.paymentDate.replace(/[/\s]/g, '-');
        const filename = `receipt-${paymentDate}-${item.paymentId}.pdf`;

        // Method 1: If receipt is HTML content
        if (typeof receipt === 'string' && receipt.includes('<')) {
          await this.downloadHtmlAsPdf(receipt, filename);
        }
        // Method 2: If receipt is a PDF URL or blob
        else if (receipt.url || receipt instanceof Blob) {
          await this.downloadPdfFromUrl(receipt, filename);
        }
        // Method 3: If receipt is base64 or other format
        else {
          await this.downloadPdfFromData(receipt, filename);
        }

        this.showDownloadStatus('success', 'Receipt downloaded successfully!');
      } catch (err) {
        console.error(err);
        this.showDownloadStatus('error', 'Failed to download receipt. Please try again.');
      } finally {
        this.downloadingId = null;

        // Clear status after 5 seconds
        setTimeout(() => {
          this.downloadStatus = null;
        }, 5000);
      }
    },

    // Method 1: Download HTML as PDF
    async downloadHtmlAsPdf(htmlContent, filename) {
      // Create a temporary container for the HTML
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      element.style.padding = '20px';
      document.body.appendChild(element);

      const options = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          letterRendering: true,
          useCORS: true
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      try {
        await html2pdf().set(options).from(element).save();
      } finally {
        // Clean up
        document.body.removeChild(element);
      }
    },

    // Method 2: Download PDF from URL or Blob
    async downloadPdfFromUrl(pdfData, filename) {
      let blob;

      if (pdfData.url) {
        // Fetch PDF from URL
        const response = await fetch(pdfData.url);
        blob = await response.blob();
      } else if (pdfData instanceof Blob) {
        blob = pdfData;
      } else {
        throw new Error('Invalid PDF data format');
      }

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    },

    // Method 3: Download PDF from base64 or other data
    async downloadPdfFromData(data, filename) {
      // Convert base64 to blob if needed
      let blob;

      if (typeof data === 'string' && data.startsWith('base64,')) {
        // Handle base64
        const base64Data = data.replace('base64,', '');
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: 'application/pdf' });
      } else {
        // Assume it's already a blob or array buffer
        blob = new Blob([data], { type: 'application/pdf' });
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    },

    showDownloadStatus(type, message) {
      this.downloadStatus = { type, message };

      this.showSnackbar({
        text: message,
        color: type === 'success' ? 'green' : 'red'
      });
    },
    handleShowUpdatePaymentMethod() {
      if (this.customerId) {
        this.showSnackbar({
          text: 'Customer Id not found!',
          color: 'red'
        });
      }
      this.isShowUpdatePaymentMethod = !this.isShowUpdatePaymentMethod;
    }
  }
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}
</style>
